import {
  Alert,
  Button,
  Card,
  Descriptions,
  Form,
  Input,
  Popconfirm,
  Space,
  Table,
  Tag,
  message,
} from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '@/services/api.service';
import { useAuthStore } from '@/store/authStore';
import EducationPageChrome from '@/pages/education/components/EducationPageChrome';
import {
  EducationGradeSelect,
  EducationSessionSelect,
} from '@/pages/education/components/EducationLookupSelects';

type ApiError = { response?: { data?: { message?: string } } };

type BatchItem = {
  id: string;
  student_id: string;
  admission_no?: string | null;
  first_name?: string | null;
  last_name?: string | null;
  source_grade_name?: string | null;
  proposed_grade_id?: string | null;
  proposed_grade_name?: string | null;
  result_enrollment_id?: string | null;
};

type Batch = {
  id: string;
  status: string;
  record_version: number;
  school_decision_reason: string;
  source_session_id: string;
  target_session_id: string;
  source_session_name?: string | null;
  target_session_name?: string | null;
  can_edit?: boolean;
  items?: BatchItem[];
};

type Preview = {
  eligible: number;
  exceptions: Array<{ item_id: string; student_id: string; code: string }>;
  status?: string;
};

function statusColor(status?: string): string | undefined {
  if (status === 'executed') return 'green';
  if (status === 'approved') return 'blue';
  if (status === 'pending_approval') return 'gold';
  return 'default';
}

export default function ProgressionDetailPage() {
  const { t } = useTranslation('education');
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const canManage = Boolean(
    useAuthStore((state) => state.user?.permissions?.includes('education.progression.manage'))
  );
  const isNew = !id || id === 'new';
  const [createForm] = Form.useForm();
  const [editForm] = Form.useForm();
  const [itemGrades, setItemGrades] = useState<Record<string, string | undefined>>({});

  const query = useQuery({
    queryKey: ['education-progression-batch', id],
    enabled: !isNew,
    queryFn: () => api.get<Batch>(`/education/progression-batches/${id}`),
  });
  const batch = query.data?.data;

  const preview = useQuery({
    queryKey: ['education-progression-preview', id],
    enabled: !isNew && Boolean(id),
    queryFn: () => api.post<Preview>(`/education/progression-batches/${id}/preview`, {}),
  });

  useEffect(() => {
    if (!batch) return;
    editForm.setFieldsValue({
      school_decision_reason: batch.school_decision_reason,
      target_session_id: batch.target_session_id,
      record_version: batch.record_version,
    });
    const next: Record<string, string | undefined> = {};
    for (const item of batch.items || []) {
      next[item.id] = item.proposed_grade_id || undefined;
    }
    setItemGrades(next);
  }, [batch, editForm]);

  const create = useMutation({
    mutationFn: (values: Record<string, string>) =>
      api.post<{ id: string }>('/education/progression-batches', values),
    onSuccess: (result) => {
      message.success(t('progressionBatchCreated', { defaultValue: 'Progression batch created' }));
      navigate(`/education/progression/${result.data?.id}`);
    },
    onError: (error: ApiError) => message.error(error.response?.data?.message || t('unavailable', { status: '' })),
  });

  const save = useMutation({
    mutationFn: (values: {
      school_decision_reason: string;
      target_session_id: string;
      record_version: number;
    }) =>
      api.patch<Batch>(`/education/progression-batches/${id}`, {
        ...values,
        item_grades: Object.entries(itemGrades).map(([item_id, proposed_grade_id]) => ({
          item_id,
          proposed_grade_id: proposed_grade_id || null,
        })),
      }),
    onSuccess: () => {
      message.success(t('saved', { defaultValue: 'Saved' }));
      queryClient.invalidateQueries({ queryKey: ['education-progression-batch', id] });
      queryClient.invalidateQueries({ queryKey: ['education-progression-preview', id] });
    },
    onError: (error: ApiError) => message.error(error.response?.data?.message || t('unavailable', { status: '' })),
  });

  const remove = useMutation({
    mutationFn: () =>
      api.delete(`/education/progression-batches/${id}`, {
        data: { record_version: batch?.record_version },
      }),
    onSuccess: () => {
      message.success(t('deleted', { defaultValue: 'Deleted' }));
      navigate('/education/progression');
    },
    onError: (error: ApiError) => message.error(error.response?.data?.message || t('unavailable', { status: '' })),
  });

  const submit = useMutation({
    mutationFn: () =>
      api.post(`/education/progression-batches/${id}/submit`, { record_version: batch?.record_version }),
    onSuccess: () => {
      message.success(t('confirmSubmitted', { defaultValue: 'Submitted for approval' }));
      queryClient.invalidateQueries({ queryKey: ['education-progression-batch', id] });
    },
    onError: (error: ApiError) => message.error(error.response?.data?.message || t('unavailable', { status: '' })),
  });

  const execute = useMutation({
    mutationFn: () => api.post(`/education/progression-batches/${id}/execute`, {}),
    onSuccess: () => {
      message.success(t('progressionExecuted', { defaultValue: 'Progression executed' }));
      queryClient.invalidateQueries({ queryKey: ['education-progression-batch', id] });
      queryClient.invalidateQueries({ queryKey: ['education-progression-preview', id] });
    },
    onError: (error: ApiError) => message.error(error.response?.data?.message || t('unavailable', { status: '' })),
  });

  const exceptionCount = preview.data?.data?.exceptions?.length ?? 0;
  const eligible = preview.data?.data?.eligible ?? batch?.items?.length ?? 0;
  const canEdit = Boolean(canManage && (isNew || batch?.can_edit));

  const title = useMemo(() => {
    if (isNew) return t('newProgressionBatch', { defaultValue: 'New progression batch' });
    return t('progressionTitle');
  }, [isNew, t]);

  return (
    <EducationPageChrome
      title={title}
      description={t('progressionIntro')}
      tags={
        !isNew ? (
          <>
            <Tag color={statusColor(batch?.status)}>{batch?.status || '—'}</Tag>
            <Tag color="green">
              {eligible} {t('eligible')}
            </Tag>
            <Tag color={exceptionCount ? 'orange' : 'default'}>
              {exceptionCount} {t('exception')}
            </Tag>
          </>
        ) : undefined
      }
      extra={
        <Space wrap>
          <Button onClick={() => navigate('/education/progression')}>{t('backToProgression')}</Button>
          {!isNew ? (
            <Button onClick={() => navigate(`/education/progression/${id}/exceptions`)}>
              {t('progressionExceptions')}
            </Button>
          ) : null}
          {!isNew && canManage && batch?.status === 'draft' ? (
            <Button type="primary" loading={submit.isPending} onClick={() => submit.mutate()}>
              {t('submitForApproval', { defaultValue: 'Submit for approval' })}
            </Button>
          ) : null}
          {!isNew && canManage && batch?.status === 'approved' ? (
            <Button type="primary" loading={execute.isPending} onClick={() => execute.mutate()}>
              {t('executeProgression', { defaultValue: 'Execute progression' })}
            </Button>
          ) : null}
          {!isNew && canManage && batch?.status === 'draft' ? (
            <Popconfirm
              title={t('deleteProgressionConfirm', {
                defaultValue: 'Delete this draft progression batch?',
              })}
              onConfirm={() => remove.mutate()}
            >
              <Button danger loading={remove.isPending}>
                {t('delete')}
              </Button>
            </Popconfirm>
          ) : null}
        </Space>
      }
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Alert type="info" showIcon message={t('progressionIntro')} />

        {isNew ? (
          <Card size="small" title={t('createBatch', { defaultValue: 'Create batch' })}>
            <Form
              form={createForm}
              layout="vertical"
              onFinish={(values) => create.mutate(values)}
              style={{ maxWidth: 560 }}
            >
              <Form.Item name="source_session_id" label={t('sourceSession')} rules={[{ required: true }]}>
                <EducationSessionSelect />
              </Form.Item>
              <Form.Item name="target_session_id" label={t('targetSession')} rules={[{ required: true }]}>
                <EducationSessionSelect />
              </Form.Item>
              <Form.Item
                name="school_decision_reason"
                label={t('schoolDecision')}
                rules={[{ required: true, message: t('schoolDecisionRequired', { defaultValue: 'School decision is required' }) }]}
              >
                <Input.TextArea rows={3} maxLength={500} />
              </Form.Item>
              <Button type="primary" htmlType="submit" loading={create.isPending} disabled={!canManage}>
                {t('save')}
              </Button>
            </Form>
          </Card>
        ) : (
          <>
            <Card size="small" title={t('batchDetails', { defaultValue: 'Batch details' })} loading={query.isLoading}>
              <Descriptions column={{ xs: 1, md: 2 }} size="small" style={{ marginBottom: 16 }}>
                <Descriptions.Item label={t('sourceSession')}>
                  {batch?.source_session_name || batch?.source_session_id || '—'}
                </Descriptions.Item>
                <Descriptions.Item label={t('targetSession')}>
                  {batch?.target_session_name || batch?.target_session_id || '—'}
                </Descriptions.Item>
                <Descriptions.Item label={t('status')}>
                  <Tag color={statusColor(batch?.status)}>{batch?.status || '—'}</Tag>
                </Descriptions.Item>
                <Descriptions.Item label={t('students', { defaultValue: 'Students' })}>
                  {batch?.items?.length ?? 0}
                </Descriptions.Item>
              </Descriptions>
              <Form
                form={editForm}
                layout="vertical"
                onFinish={(values) => save.mutate(values)}
                style={{ maxWidth: 640 }}
                disabled={!canEdit}
              >
                <Form.Item name="record_version" hidden>
                  <Input />
                </Form.Item>
                <Form.Item name="target_session_id" label={t('targetSession')} rules={[{ required: true }]}>
                  <EducationSessionSelect disabled={!canEdit} />
                </Form.Item>
                <Form.Item
                  name="school_decision_reason"
                  label={t('schoolDecision')}
                  rules={[{ required: true }]}
                >
                  <Input.TextArea rows={3} maxLength={500} />
                </Form.Item>
                {canEdit ? (
                  <Button type="primary" htmlType="submit" loading={save.isPending}>
                    {t('save')}
                  </Button>
                ) : null}
              </Form>
            </Card>

            <Card size="small" title={t('progressionStudents', { defaultValue: 'Students in batch' })}>
              <Table
                rowKey="id"
                size="small"
                loading={query.isLoading}
                dataSource={batch?.items || []}
                pagination={{ pageSize: 20, showSizeChanger: true }}
                columns={[
                  {
                    title: t('admissionNo'),
                    dataIndex: 'admission_no',
                    width: 120,
                    render: (value?: string | null) => value || '—',
                  },
                  {
                    title: t('student'),
                    render: (_: unknown, row: BatchItem) =>
                      [row.first_name, row.last_name].filter(Boolean).join(' ') || row.student_id,
                  },
                  {
                    title: t('currentGrade', { defaultValue: 'Current grade' }),
                    dataIndex: 'source_grade_name',
                    render: (value?: string | null) => value || '—',
                  },
                  {
                    title: t('proposedGrade', { defaultValue: 'Proposed grade' }),
                    render: (_: unknown, row: BatchItem) =>
                      canEdit ? (
                        <EducationGradeSelect
                          value={itemGrades[row.id]}
                          allowClear
                          style={{ minWidth: 180 }}
                          onChange={(value) =>
                            setItemGrades((prev) => ({
                              ...prev,
                              [row.id]: typeof value === 'string' ? value : undefined,
                            }))
                          }
                        />
                      ) : (
                        row.proposed_grade_name || row.proposed_grade_id || '—'
                      ),
                  },
                  {
                    title: t('resultEnrollment', { defaultValue: 'Result enrollment' }),
                    dataIndex: 'result_enrollment_id',
                    render: (value?: string | null) => value || '—',
                  },
                ]}
              />
            </Card>

            <Card size="small" title={t('progressionExceptions')}>
              <Table
                rowKey={(row) => `${row.item_id}-${row.code}`}
                size="small"
                loading={preview.isLoading}
                dataSource={preview.data?.data?.exceptions || []}
                pagination={false}
                locale={{
                  emptyText: t('noExceptions', { defaultValue: 'No exceptions in this preview' }),
                }}
                columns={[
                  { title: t('studentId'), dataIndex: 'student_id' },
                  {
                    title: t('exception'),
                    dataIndex: 'code',
                    render: (value: string) => <Tag color="orange">{value}</Tag>,
                  },
                ]}
              />
            </Card>
          </>
        )}
      </Space>
    </EducationPageChrome>
  );
}
